import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet, BookOpen, Shield, Truck, Ticket, ArrowRight, CheckCircle, HelpCircle } from 'lucide-react';

const articlePaths: Record<string, string> = {
  pt: '/artigo/',
  en: '/article/',
  de: '/artikel/',
};

export default function ContentSections() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'pt';

  const guideCards = [t('guideCard1'), t('guideCard2'), t('guideCard3'), t('guideCard4'), t('guideCard5'), t('guideCard6')];
  const safetyCards = [t('safetyCard1'), t('safetyCard2'), t('safetyCard3'), t('safetyCard4'), t('safetyCard5'), t('safetyCard6'), t('safetyCard7')];

  const reviewAspects = [
    { name: t('reviewAspect1'), desc: t('reviewAspect1Desc') },
    { name: t('reviewAspect2'), desc: t('reviewAspect2Desc') },
    { name: t('reviewAspect3'), desc: t('reviewAspect3Desc') },
    { name: t('reviewAspect4'), desc: t('reviewAspect4Desc') },
    { name: t('reviewAspect5'), desc: t('reviewAspect5Desc') },
    { name: t('reviewAspect6'), desc: t('reviewAspect6Desc') },
    { name: t('reviewAspect7'), desc: t('reviewAspect7Desc') },
  ];

  const features = [t('comparisonFeature1'), t('comparisonFeature2'), t('comparisonFeature3'), t('comparisonFeature4'), t('comparisonFeature5')];
  const platforms = [t('comparisonPlatform1'), t('comparisonPlatform2'), t('comparisonPlatform3'), t('comparisonPlatform4')];

  return (
    <div className="space-y-0">
      {/* What Is Section */}
      <section className="py-14 md:py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-5">
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-brand-700" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-900">{t('whatIsTitle')}</h2>
              <p className="text-warm-600 leading-relaxed">{t('whatIsDesc')}</p>
              <a
                href={articlePaths[currentLang] || '/artigo/'}
                onClick={(e) => { e.preventDefault(); navigate(articlePaths[currentLang] || '/artigo/'); }}
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors"
              >
                {t('spreadsheetCta')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-warm-200">
              <img
                src="https://readdy.ai/api/search-image?query=Top%20view%20of%20a%20tidy%20product%20spreadsheet%20layout%20on%20a%20light%20wood%20desk%20with%20a%20tablet%20fabric%20swatches%20and%20colorful%20sticky%20notes%2C%20soft%20natural%20light%2C%20mint%20and%20cream%20tones%2C%20editorial%20photography&width=800&height=600&seq=whatis-2026&orientation=landscape&nocache=true"
                alt="CSSBuy Spreadsheet"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-brand-600" />
            </div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900">{t('guidesTitle')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {guideCards.map((card, idx) => (
              <div
                key={idx}
                className="group p-5 rounded-xl border border-warm-200 hover:border-brand-300 bg-white hover:bg-brand-50 transition-all duration-200 cursor-pointer"
                onClick={() => navigate(articlePaths[currentLang] || '/artigo/')}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-warm-700 group-hover:text-brand-700">{card}</span>
                  <ArrowRight className="w-4 h-4 text-warm-400 group-hover:text-brand-500 transition-colors" />
                </div>
                <div className="h-1 w-8 rounded-full bg-warm-200 group-hover:bg-brand-300 transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review Section */}
      <section className="py-14 md:py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900 mb-8">{t('reviewTitle')}</h2>
          <div className="overflow-x-auto rounded-xl border border-warm-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warm-200">
                  <th className="text-left px-4 py-3 font-semibold text-warm-700 bg-warm-50">Aspect</th>
                  <th className="text-left px-4 py-3 font-semibold text-warm-700 bg-warm-50">What to analyze</th>
                </tr>
              </thead>
              <tbody>
                {reviewAspects.map((aspect, idx) => (
                  <tr key={idx} className="border-b border-warm-100 last:border-0 hover:bg-brand-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-warm-800">{aspect.name}</td>
                    <td className="px-4 py-3 text-warm-600">{aspect.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6">
            <a
              href={articlePaths[currentLang] || '/artigo/'}
              onClick={(e) => { e.preventDefault(); navigate(articlePaths[currentLang] || '/artigo/'); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {t('reviewCta')} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-warm-200 order-2 md:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Stack%20of%20brown%20kraft%20shipping%20boxes%20with%20tape%20on%20a%20soft%20beige%20background%2C%20small%20parcel%20and%20measuring%20tape%20nearby%2C%20soft%20studio%20lighting%2C%20clean%20logistics%20aesthetic%2C%20editorial%20photography&width=800&height=600&seq=shipping-2026&orientation=landscape&nocache=true"
                alt="Shipping"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-5 order-1 md:order-2">
              <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                <Truck className="w-5 h-5 text-brand-600" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-900">{t('shippingTitle')}</h2>
              <p className="text-warm-600 leading-relaxed">{t('shippingDesc')}</p>
              <a
                href={articlePaths[currentLang] || '/artigo/'}
                onClick={(e) => { e.preventDefault(); navigate(articlePaths[currentLang] || '/artigo/'); }}
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors"
              >
                {t('shippingCta')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Coupon Section */}
      <section className="py-14 md:py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-5">
              <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-accent-600" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-900">{t('couponTitle')}</h2>
              <p className="text-warm-600 leading-relaxed">{t('couponDesc')}</p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 hover:bg-accent-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {t('couponCta')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-warm-200">
              <img
                src="https://readdy.ai/api/search-image?query=Hands%20holding%20a%20discount%20coupon%20with%20scissors%20and%20a%20gift%20ribbon%20on%20a%20soft%20mint%20background%2C%20flat%20lay%2C%20soft%20studio%20lighting%2C%20modern%20promotional%20editorial%20aesthetic&width=800&height=600&seq=coupon-2026&orientation=landscape&nocache=true"
                alt="Coupons"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-brand-600" />
            </div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900">{t('safetyTitle')}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
            {safetyCards.map((card, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-warm-50 border border-warm-200">
                <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" />
                <span className="text-sm text-warm-700">{card}</span>
              </div>
            ))}
          </div>
          <a
            href={articlePaths[currentLang] || '/artigo/'}
            onClick={(e) => { e.preventDefault(); navigate(articlePaths[currentLang] || '/artigo/'); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {t('safetyCta')} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-14 md:py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900 mb-8">{t('comparisonTitle')}</h2>
          <div className="overflow-x-auto rounded-xl border border-warm-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warm-200">
                  <th className="text-left px-4 py-3 font-semibold text-warm-700 bg-warm-50">Feature</th>
                  {platforms.map((p, i) => (
                    <th key={i} className={`text-left px-4 py-3 font-semibold ${i === 0 ? 'text-brand-700 bg-brand-50' : 'text-warm-700 bg-warm-50'}`}>{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feat, idx) => (
                  <tr key={idx} className="border-b border-warm-100 last:border-0 hover:bg-brand-50/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-warm-800">{feat}</td>
                    {platforms.map((_, i) => (
                      <td key={i} className="px-4 py-3 text-warm-500">{t('comparisonVerify')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-brand-600" />
            </div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900">FAQ</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[t('faqQ1'), t('faqQ2'), t('faqQ3'), t('faqQ4')].map((q, idx) => (
              <div key={idx} className="p-5 rounded-xl border border-warm-200 bg-warm-50 hover:bg-brand-50 transition-colors">
                <h4 className="font-medium text-warm-800 mb-2">{q}</h4>
                <p className="text-sm text-warm-500">
                  {idx === 0 ? t('faqA1') : idx === 1 ? t('faqA2') : idx === 2 ? t('faqA3') : t('faqA4')}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-warm-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20flowing%20fabric%20waves%20in%20cream%20sage%20green%20and%20beige%20tones%2C%20abstract%20minimal%20background%20with%20gentle%20texture%2C%20calm%20airy%20aesthetic%2C%20high%20key%20lighting&width=1600&height=900&seq=final-cta-2026&orientation=landscape&nocache=true"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-display text-2xl md:text-4xl font-bold text-white mb-4">{t('finalCtaTitle')}</h2>
          <p className="text-warm-300 max-w-2xl mx-auto mb-8 leading-relaxed">{t('finalCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-500 text-white rounded-xl font-medium text-sm transition-colors"
            >
              {t('finalCtaBtn1')} <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium text-sm transition-colors border border-white/20"
            >
              {t('finalCtaBtn2')} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}